import { useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { appConfirm } from "../../components/ConfirmDialog";
import { EmailFlash } from "../../components/EmailFlash";
import { Loader2, Plus, Trash2, Save, History, Eye, Send as SendIcon, RotateCcw } from "lucide-react";

const gbp = (n) => "£" + Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 });
const gbp2 = (n) => "£" + Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dtGB = (d) => d ? new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const RIBBON = [
  { key: "enquiry", label: "Enquiry" },
  { key: "estimate", label: "Estimate" },
  { key: "invoice", label: "Invoice" },
  { key: "paid", label: "Paid" },
];

export function EstimateRibbon({ status }) {
  const idx = Math.max(1, RIBBON.findIndex(r => r.key === (status || "estimate")));
  return (
    <div className="flex items-center flex-wrap" data-testid="estimate-ribbon">
      {RIBBON.map((r, i) => (
        <div key={r.key} className="flex items-center">
          {i > 0 && <div className={`w-6 h-0.5 ${i <= idx ? "bg-[#C79A3B]" : "bg-[#E5E7EB]"}`} />}
          <span data-testid={`estimate-ribbon-${r.key}`}
            className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-full ${
              i < idx ? "bg-[#D8EED8] text-[#1F6B3B]" : i === idx ? "bg-[#12263A] text-white" : "bg-[#F0EAE0] text-[#9CA3AF]"}`}>
            {r.label}{i < idx ? " ✓" : ""}
          </span>
        </div>
      ))}
    </div>
  );
}

export function QuoteEstimate({ quoteId, data, onSaved }) {
  const initial = useMemo(() => {
    if (data?.estimate?.line_items?.length) {
      return data.estimate.line_items.map(li => ({
        id: li.id || crypto.randomUUID(),
        name: li.name || li.label || "",
        amount: Number(li.amount ?? li.value ?? 0),
      }));
    }
    // New estimate: never seed from the internal configurator cost breakdown —
    // clients only ever see client-friendly grouped lines + staff-added items.
    const grouped = (data?.breakdown?.client_lines || []).filter(li => Number(li.amount || 0) !== 0);
    const src = grouped.length ? grouped
      : [{ name: "Extension works as configured", amount: Number(data?.total_inc_vat || 0) }];
    return src.map(li => ({ id: crypto.randomUUID(), name: li.name || "", amount: Number(li.amount || 0) }));
  }, [data]);

  const [rows, setRows] = useState(initial);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const status = data?.estimate_status || "estimate";
  const revisions = [...(data?.estimate_revisions || [])].reverse();
  const hasEstimate = !!(data?.estimate?.line_items?.length);
  const docType = (status === "invoice" || status === "paid") ? "invoice" : "estimate";
  const docLabel = docType === "invoice" ? "Invoice" : "Estimate";
  const sentAt = docType === "invoice" ? data?.invoice_sent_at : data?.estimate_sent_at;
  const sentBy = docType === "invoice" ? data?.invoice_sent_by : data?.estimate_sent_by;
  const previewUrl = `${api.defaults.baseURL}/admin/quote/${encodeURIComponent(quoteId)}/document-pdf?doc=${docType}`;

  const total = rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
  const exVat = total / 1.2;
  const dirty = JSON.stringify(rows.map(r => [r.name, Number(r.amount) || 0]))
    !== JSON.stringify(initial.map(r => [r.name, r.amount]));

  const edit = (id, patch) => setRows(rs => rs.map(r => r.id === id ? { ...r, ...patch } : r));
  const remove = (id) => setRows(rs => rs.filter(r => r.id !== id));
  const addRow = () => setRows(rs => [...rs, { id: crypto.randomUUID(), name: "", amount: 0 }]);

  const groupedLines = useMemo(
    () => (data?.breakdown?.client_lines || []).filter(li => Number(li.amount || 0) !== 0),
    [data]
  );
  const resetToGrouped = async () => {
    if (groupedLines.length === 0) {
      toast.error("No grouped client lines available for this quote.");
      return;
    }
    if (!(await appConfirm({
      title: "Reset to grouped client lines?",
      description: `Replaces every current line with the ${groupedLines.length} client-friendly grouped line${groupedLines.length === 1 ? "" : "s"} (Extension build / Fittings & finishes / Site works). The client will only see these headline groups — internal costings are hidden. Nothing is saved until you click "Save revision".`,
      confirmLabel: "Reset lines",
    }))) return;
    setRows(groupedLines.map(li => ({ id: crypto.randomUUID(), name: li.name || "", amount: Number(li.amount || 0) })));
    toast.success("Line items reset to client-friendly groups — click Save revision to lock it in.");
  };

  const save = async () => {
    if (rows.length === 0) { toast.error("Add at least one line item"); return; }
    if (rows.some(r => !String(r.name).trim())) { toast.error("Every line item needs a description"); return; }
    setSaving(true);
    try {
      const r = await api.put(`/admin/quote/${encodeURIComponent(quoteId)}/estimate`, {
        line_items: rows.map(x => ({ id: x.id, name: String(x.name).trim(), amount: Number(x.amount) || 0 })),
        note: note.trim(),
      });
      toast.success(`Revision ${r.data.revision.revision} saved — new total ${gbp(r.data.estimate.total_inc_vat)}`);
      setNote("");
      onSaved && onSaved(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the estimate"); }
    setSaving(false);
  };

  const setStatus = async (s) => {
    setStatusSaving(true);
    try {
      await api.put(`/admin/quote/${encodeURIComponent(quoteId)}/estimate-status`, { status: s });
      toast.success(`Marked as ${s}`);
      onSaved && onSaved({ estimate_status: s });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update status"); }
    setStatusSaving(false);
  };

  const sendDoc = async () => {
    if (!(await appConfirm({
      title: `Email the ${docLabel.toLowerCase()} to ${data?.email}?`,
      description: `The ${docLabel} PDF (revision ${data?.estimate?.revision}) will be attached, with a link for the client to sign in to their portal and accept${docType === "estimate" ? " — sending an invoice later will auto-advance the status" : ""}.`,
      confirmLabel: `Send ${docLabel.toLowerCase()}`,
    }))) return;
    setSending(true);
    try {
      const r = await api.post(`/admin/quote/${encodeURIComponent(quoteId)}/document/send`, { doc: docType });
      if (r.data.sent) {
        toast.success(`${docLabel} emailed to ${r.data.to}`);
        onSaved && onSaved({
          estimate_status: r.data.estimate_status,
          [`${docType}_sent_at`]: r.data[`${docType}_sent_at`],
          [`${docType}_sent_by`]: r.data[`${docType}_sent_by`],
        });
      } else {
        toast.error("Email could not be sent — check the Gmail connection in Settings → Email");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || `Could not send the ${docLabel.toLowerCase()}`); }
    setSending(false);
  };

  return (
    <div className="space-y-4" data-testid="quote-detail-estimate">
      {data?.estimate_accepted_at && (
        <div className="border border-[#B7DEC6] bg-[#E7F4EC] px-4 py-2.5 text-xs font-bold text-[#1F6B3B]" data-testid="estimate-accepted-banner">
          ✓ Client accepted this revised estimate on {dtGB(data.estimate_accepted_at)}
          {data.estimate_accepted_booking_ref ? ` — booking ${data.estimate_accepted_booking_ref}` : ""}
        </div>
      )}
      <div className="border border-[#E5E7EB] bg-white p-4 flex items-center justify-between gap-3 flex-wrap">
        <EstimateRibbon status={status} />
        <div className="flex gap-2">
          {status === "estimate" && (
            <button data-testid="estimate-status-invoice" disabled={statusSaving} onClick={() => setStatus("invoice")}
              className="px-3 py-1.5 text-xs font-bold border border-[#C79A3B] text-[#12263A] hover:bg-[#C79A3B] hover:text-white disabled:opacity-50">
              Mark as Invoice →
            </button>
          )}
          {status === "invoice" && (
            <>
              <button data-testid="estimate-status-back-estimate" disabled={statusSaving} onClick={() => setStatus("estimate")}
                className="px-3 py-1.5 text-xs font-bold border border-[#E5E7EB] text-[#6B7280] hover:border-[#12263A] disabled:opacity-50">
                ← Back to Estimate
              </button>
              <button data-testid="estimate-status-paid" disabled={statusSaving} onClick={() => setStatus("paid")}
                className="px-3 py-1.5 text-xs font-bold bg-[#1F6B3B] text-white hover:bg-[#155029] disabled:opacity-50">
                Mark as Paid ✓
              </button>
            </>
          )}
          {status === "paid" && (
            <button data-testid="estimate-status-back-invoice" disabled={statusSaving} onClick={() => setStatus("invoice")}
              className="px-3 py-1.5 text-xs font-bold border border-[#E5E7EB] text-[#6B7280] hover:border-[#12263A] disabled:opacity-50">
              ← Back to Invoice
            </button>
          )}
        </div>
      </div>

      <div className="border border-[#E5E7EB] bg-white">
        <div className="px-4 pt-4">
          <div className="label-eyebrow mb-1">Editable line items (inc VAT)</div>
          <p className="text-[11px] text-[#6B7280] mb-3">
            Click any description or price to edit it — e.g. extras agreed at the site visit. The revised total
            instantly becomes this client&apos;s official quote total, used by the £499 booking checkout and promote-to-job.
            New estimates start from client-friendly grouped lines — the internal configurator cost breakdown is never shown to the client.
          </p>
        </div>
        <div className="px-4 pb-2 space-y-1.5">
          {rows.map((r, i) => (
            <div key={r.id} className="flex items-center gap-2" data-testid={`estimate-line-${i}`}>
              <input data-testid={`estimate-line-${i}-name`} value={r.name}
                onChange={(e) => edit(r.id, { name: e.target.value })}
                placeholder="Line item description…"
                className="flex-1 border border-[#E5E7EB] px-3 py-2 text-sm focus:border-[#C79A3B] outline-none" />
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-[#9CA3AF]">£</span>
                <input data-testid={`estimate-line-${i}-amount`} type="number" step="0.01" value={r.amount}
                  onChange={(e) => edit(r.id, { amount: e.target.value })}
                  className="w-32 border border-[#E5E7EB] pl-6 pr-2 py-2 text-sm text-right tabular-nums focus:border-[#C79A3B] outline-none" />
              </div>
              <button data-testid={`estimate-line-${i}-remove`} onClick={() => remove(r.id)}
                title="Remove line" className="text-[#9CA3AF] hover:text-[#C0392B] p-1.5">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <div className="flex items-center gap-3 flex-wrap py-1.5">
            <button data-testid="estimate-add-line" onClick={addRow}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#C79A3B] hover:text-[#A87E28]">
              <Plus size={13} /> Add line item
            </button>
            {groupedLines.length > 0 && (
              <button data-testid="estimate-reset-grouped" onClick={resetToGrouped}
                title="Hide the internal line-by-line costings and show only the client-friendly grouped totals (Extension build / Fittings & finishes / Site works)."
                className="inline-flex items-center gap-1.5 text-xs font-bold text-[#6B7280] hover:text-[#12263A]">
                <RotateCcw size={13} /> Reset to grouped client lines
              </button>
            )}
          </div>
        </div>
        <div className="border-t border-[#E5E7EB] bg-[#F8F6F2] px-4 py-3 flex items-end justify-between gap-3 flex-wrap">
          <div className="text-xs text-[#6B7280]">
            Ex VAT {gbp2(exVat)} · VAT (20%) {gbp2(total - exVat)}
          </div>
          <div className="text-right">
            <div className="label-eyebrow">Revised total (inc VAT)</div>
            <div className="text-2xl font-black text-[#12263A] tabular-nums" data-testid="estimate-total">{gbp(total)}</div>
            {dirty && <div className="text-[11px] text-[#B45309] font-bold">Unsaved changes</div>}
          </div>
        </div>
        <div className="border-t border-[#E5E7EB] px-4 py-3 flex items-center gap-2 flex-wrap">
          <input data-testid="estimate-note" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Optional revision note (e.g. 'added steel beam agreed at site visit')…"
            className="flex-1 min-w-[220px] border border-[#E5E7EB] px-3 py-2 text-sm" />
          <button data-testid="estimate-save" onClick={save} disabled={saving || rows.length === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold bg-[#12263A] text-white hover:bg-[#0C1B2B] disabled:opacity-50">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save revision
          </button>
        </div>
      </div>

      <div className="border border-[#E5E7EB] bg-white p-4" data-testid="estimate-send-panel">
        <div className="label-eyebrow mb-2">Preview &amp; send to client</div>
        {!hasEstimate ? (
          <p className="text-xs text-[#9CA3AF]" data-testid="estimate-send-info-no-revision">
            Save a revision first — preview and send become available once at least one revision exists.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <a data-testid="estimate-preview-btn" href={previewUrl} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-[#12263A] text-[#12263A] hover:bg-[#12263A] hover:text-white transition-colors">
                <Eye size={13} /> Preview {docLabel} PDF
              </a>
              <button data-testid="estimate-send-btn" onClick={sendDoc} disabled={!data?.email || sending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-[#C79A3B] text-white hover:bg-[#A87E28] disabled:opacity-40">
                {sending ? <Loader2 size={13} className="animate-spin" /> : <SendIcon size={13} />} Send {docLabel} to client
              </button>
              <EmailFlash text={`Emails the ${docLabel} PDF to the client with a portal link`} />
            </div>
            {!data?.email && (
              <p className="text-[11px] text-[#C0392B] font-bold mt-2" data-testid="estimate-send-info-no-email">
                Can&apos;t send — no email address on this file. Add one via <b>Edit</b> on the Contact card (Overview tab).
              </p>
            )}
            {sentAt && (
              <p className="text-[11px] text-[#1F6B3B] font-bold mt-2" data-testid="estimate-sent-stamp">
                {docLabel} sent {dtGB(sentAt)}{sentBy ? ` · ${sentBy}` : ""}
              </p>
            )}
            {dirty && (
              <p className="text-[11px] text-[#B45309] mt-2" data-testid="estimate-send-info-unsaved">
                You have unsaved changes — save the revision before sending so the client receives the latest figures.
              </p>
            )}
          </>
        )}
      </div>

      <div className="border border-[#E5E7EB] bg-white p-4" data-testid="estimate-revisions">
        <div className="label-eyebrow mb-2 flex items-center gap-1.5"><History size={12} /> Revision history</div>
        {revisions.length === 0 ? (
          <p className="text-xs text-[#9CA3AF]">No revisions yet — the estimate still matches the client&apos;s original configured quote.</p>
        ) : (
          <ul className="space-y-2">
            {revisions.map(rv => (
              <li key={rv.revision} data-testid={`estimate-revision-${rv.revision}`}
                className="flex items-start justify-between gap-3 border-b border-[#F0EAE0] pb-2 last:border-0 last:pb-0">
                <div className="text-xs">
                  <span className="font-black text-[#12263A]">Rev {rv.revision}</span>
                  <span className="text-[#6B7280]"> · {rv.by} · {dtGB(rv.at)}</span>
                  {rv.note && <div className="text-[#374151] mt-0.5">{rv.note}</div>}
                </div>
                <div className="text-sm font-black text-[#12263A] tabular-nums shrink-0">{gbp(rv.total_inc_vat)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
